import React, { useEffect, useState } from "react";

import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Hidden,
  IconButton,
  MenuItem,
  Paper,
  Slide,
  Snackbar,
  SwipeableDrawer,
  Tab,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";

import * as lab from "@material-ui/lab";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";
import { Line } from "react-chartjs-2";

import * as UserApi from "../../api/users";
import * as UsersApi from "../../api/users";

import { AdministratorEvent, LifvsUser, UserStatus } from "../../models/User";
import { SendNotificationModal } from "./modals/SendNotificationModal";
import { isOk } from "../../result";

import useStyles from "./styles";
import Top20Collection from "./collections/Top20Collection";
import DiscountsCollection from "./collections/DiscountsCollection";
import NotificationsCollection from "./collections/NotificationsCollection";

import LockUserModal from "./modals/LockUserModal";
import UnlockUserModal from "./modals/UnlockUserModal";
import DeleteUserModal from "./modals/DeleteUserModal";
import RefundModal from "./modals/RefundModal";
import AddDiscountModal from "./modals/AddDiscountModal";
import Result from "../../components/information/result";
import Alert from "../../components/information/alert";
import Progress from "../../components/information/progress";
import EventsCollection from "./collections/EventsCollection";
import moment from "moment";
import { Empty } from "./misc/Empty";
import AdministratorEventsCollection from "./collections/AdministratorEventsCollection";
import {
  AddShoppingCart,
  Delete,
  Lock,
  Message,
  Report,
  VerifiedUser
} from "@material-ui/icons";
import { ConfirmDialog } from "../../components/dialogs/ConfirmDialog";
import AddCommentModal from "./modals/AddCommentModal";
import { useNavigate } from "react-router-dom";
import {
  Campaign,
  ContentCopy,
  Equalizer,
  Fastfood,
  Info,
  LockOpen,
  Loyalty,
  Settings
} from "@mui/icons-material";
import LoyaltyIcon from "@material-ui/icons/Loyalty";
import ReceiptsCollection from "./collections/ReceiptsCollection";
import { SecuredField } from "./misc/SecuredField";
import MenuIcon from "@material-ui/icons/Menu";
import Divider from "@material-ui/core/Divider";
import CloseIcon from "@material-ui/icons/Close";
import { UserSectionCard } from "./misc/UserSectionCard";
import Modal from "../../components/dialogs/modal";
import { useI18n } from "../../hooks/useI18n";

export interface LoadingState {
  type: "LOADING";
}

export interface NotFoundState<T> {
  type: "NOT_FOUND";
  id: T;
}

export interface UnexpectedErrorState {
  type: "UNEXPECTED_ERROR";
  reason: string;
}

export interface LoadedState {
  type: "LOADED";
  user: LifvsUser;
}

export interface AddingDiscountState {
  type: "ADDING_DISCOUNT";
  user: LifvsUser;
  discountId: string;
}

export interface ShowErrorState {
  type: "SHOW_ERROR";
  user: LifvsUser;
  message: string;
}

export type UserState =
  | LoadingState
  | NotFoundState<string>
  | UnexpectedErrorState
  | LoadedState
  | AddingDiscountState
  | ShowErrorState;

export interface UserOverviewProps {
  readonly inputUserId: string;
}

export const UserOverview = ({ inputUserId }: UserOverviewProps) => {
  const navigate = useNavigate();
  const styles = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useI18n();

  const [selectedTab, setSelectedTab] = useState("userDetails");
  const selectTab = (event: React.ChangeEvent<{}>, newValue: string) => {
    setSelectedTab(newValue);
  };

  const [showCopiedUserId, setShowCopiedUserId] = useState(false);
  const [fullOrPartialModalVisible, setfullOrPartialModalVisible] =
    useState(false);

  const [fullRefundCofirmDialogVisble, setFullRefundCofirmDialogVisble] =
    useState(false);
  const [refundModalVisible, setRefundModelVisible] = useState(false);
  const [refundReceipt, setRefundReceipt] = useState(0);
  const [sendNotificationModelVisible, setSendNotificationModelVisible] =
    useState(false);
  const [lockUserModalVisible, setLockUserModalVisible] = useState(false);
  const [unlockUserModalVisible, setUnlockUserModalVisible] =
    useState<boolean>(false);

  const [updateStatusVisible, setUpdateStatusVisible] = useState({
    status: UserStatus.None,
    display: false
  });

  const [commentState, setCommentState] = useState({
    comments: [] as any,
    display: false
  });

  const [deleteUserModalVisible, setDeleteUserModalVisible] =
    useState<boolean>(false);
  const [refundReceiptErrorMessage, setRefundReceiptErrorMessage] =
    useState("");

  const userId = inputUserId || "";

  const [state, setState] = useState<UserState>({
    type: "LOADING"
  });

  const [reloadHistory, setReloadHistory] = useState(false);
  const [historyState, setHistoryState] = useState({
    administratorEvents: [] as any,
    loading: false,
    problem: null as any
  });

  const [loadingPurchases, setLoadingPurchases] = useState(false);

  // Drawer for user actions menu
  const [actionsOpen, setActionsOpen] = React.useState(false);
  const toggleActionsDrawer = () => {
    setActionsOpen(!actionsOpen);
  };

  const handleActionsOpen = () => {
    setActionsOpen(true);
  };

  const handleActionsClose = () => {
    setActionsOpen(false);
  };

  useEffect(() => {
    if (selectedTab === "adminEvents") getAdministratorEvents();
  }, [selectedTab]);

  useEffect(() => {
    if (reloadHistory) getAdministratorEvents();
  }, [reloadHistory]);

  useEffect(() => {
    if (!userId) {
      // Remove userid from url when we get an empty one
      const updatedUrl = location.pathname.replace(/^(.*)\/.*/, "$1");
      history.replaceState({ pathname: location.pathname }, "", updatedUrl);
      return;
    }

    UserApi.getById(userId).then(result => {
      switch (result.type) {
        case "NOT_FOUND":
          return setState({
            type: "NOT_FOUND",
            id: userId
          });

        case "SUCCESS":
          return setState({
            type: "LOADED",
            user: result.data
          });

        case "UNEXPECTED_HTTP_ERROR":
          return setState({
            type: "UNEXPECTED_ERROR",
            reason: `The server responded with ${result.response.status} ${result.response.statusText}, which we did not expect it to do.`
          });

        case "UNEXPECTED_ERROR":
          return setState({
            type: "UNEXPECTED_ERROR",
            reason: result.reason.toString()
          });
      }
    });
  }, [userId]);

  useEffect(() => {
    if (state.type !== "LOADED") return;
    setLoadingPurchases(true);
    UsersApi.getUserPurchases(userId).then(result => {
      if (!isOk(result)) {
        return setState({
          type: "UNEXPECTED_ERROR",
          reason: result.details.detail
        });
      }
      setLoadingPurchases(false);
      setState({
        ...state,
        user: {
          ...state.user,
          receipts: result.value.receipts,
          averagePurchaseAmount: result.value.averagePurchaseAmount,
          topPurchasedProducts: result.value.topPurchasedProducts
        }
      });
    });
  }, [state.type]);

  const getAdministratorEvents = () => {
    setReloadHistory(false);
    setHistoryState({ ...historyState, loading: true });
    UsersApi.listAdministratorEvents(userId).then(result => {
      if (!isOk(result)) {
        return setHistoryState({
          ...historyState,
          loading: false,
          problem: result.details
        });
      }

      const comments = result.value.reduce((arr: any, e) => {
        if (e.event === AdministratorEvent.AddComment) {
          arr.push({
            timestamp: e.timeStamp,
            userName: e.userName,
            comment: e.data.comment
          });
        }
        return arr;
      }, []);

      setCommentState({
        ...commentState,
        comments: comments
      });

      return setHistoryState({
        ...historyState,
        loading: false,
        administratorEvents: result.value
      });
    });
  };

  const onLockOrUnlock = () => {
    if (state.type !== "LOADED") return;

    if (state.user.status === UserStatus.Locked) {
      setUnlockUserModalVisible(true);
    } else {
      setLockUserModalVisible(true);
    }
  };

  const onDelete = () => {
    if (state.type !== "LOADED") return;

    setDeleteUserModalVisible(true);
  };

  const onUpdate = async (status: UserStatus) => {
    if (state.type !== "LOADED") return;

    setReloadHistory(false);
    const result = await UserApi.updateStatus(state.user.id, status);

    if (isOk(result)) {
      setReloadHistory(true);
      return setState({
        ...state,
        user: {
          ...state.user,
          status: status
        }
      });
    }

    return setState({
      type: "SHOW_ERROR",
      user: state.user,
      message: `An error occurred when updating status. ${result.details.status} - ${result.details.title} - ${result.details.detail}`
    });
  };

  const onEmployeeUpdate = async (employee: boolean) => {
    if (state.type !== "LOADED") return;

    setReloadHistory(false);
    const result = await UserApi.updateEmployeeStatus(state.user.id, employee);

    if (isOk(result)) {
      setReloadHistory(true);
      return setState({
        ...state,
        user: {
          ...state.user,
          employee: employee
        }
      });
    }

    return setState({
      type: "SHOW_ERROR",
      user: state.user,
      message: `An error occurred when updating status. ${result.details.status} - ${result.details.title} - ${result.details.detail}`
    });
  };

  switch (state.type) {
    case "LOADING":
      return (
        <div style={{ height: mobile ? "100%" : 500 }}>
          <Progress />
        </div>
      );

    case "NOT_FOUND":
      return (
        <Paper className={styles.userPageContainer}>
          <h2>{t("common.messages.userOverview")}</h2>

          <Result
            status="error"
            message={`It appears we could not find any user with id '${state.id}'.`}
          />
        </Paper>
      );

    case "UNEXPECTED_ERROR":
      return (
        <Paper className={styles.userPageContainer}>
          <h2>{t("common.messages.userOverview")}</h2>

          <Result status="error" message="I don't know what to tell you. :/" />
        </Paper>
      );

    case "LOADED":
    case "ADDING_DISCOUNT":
    case "SHOW_ERROR":
      const user = state.user;

      const receipts = user.receipts.map(r => ({
        createdDate: r.createdDate.toString().split("T")[0],
        amount: r.netAmount
      }));

      const accumulated = receipts.reduce(
        (prev: any, { createdDate, amount }) => {
          prev[createdDate] = (prev[createdDate] || 0) + amount;
          return prev;
        },
        {}
      );

      const data = {
        labels: Object.keys(accumulated).reverse(),
        datasets: [
          {
            label: t("common.tableHeaders.purchaseAmount"),
            backgroundColor: "rgba(255,99,132,0.2)",
            borderColor: "rgba(255,99,132,1)",
            borderWidth: 1,
            hoverBackgroundColor: "rgba(255,99,132,0.4)",
            hoverBorderColor: "rgba(255,99,132,1)",
            data: Object.values(accumulated).reverse()
          }
        ]
      };

      const showAlert = (
        title: string,
        message: string,
        closeable: boolean
      ) => (
        <Alert
          visible={true}
          severity="error"
          title={title}
          message={message}
          action={
            closeable ? (
              <IconButton
                size="small"
                onClick={() => {
                  setState({
                    ...state,
                    type: "LOADED"
                  });
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : null
          }
        />
      );

      const renderUserDetailsSection = (screenSize: "mobile" | "desktop") => (
        <Grid
          container
          spacing={2}
          direction={screenSize === "mobile" ? "column" : "row"}
        >
          <Grid item xs={12} lg={4}>
            <Card className={styles.fullHeight}>
              <CardContent>
                <Box sx={{ display: "flex" }} pb={3}>
                  <Box flexGrow={1}>
                    <div>
                      <Typography
                        variant={"caption"}
                        color={"textSecondary"}
                        style={{ textTransform: "uppercase" }}
                      >
                        Registration Date
                      </Typography>
                    </div>
                    <Typography variant={"h6"}>
                      {moment(user.registrationDate).format("YYYY-MM-DD HH:mm")}
                    </Typography>
                  </Box>
                  <div>
                    <Info className={styles.sectionIconSuccess} />
                  </div>
                </Box>
                {renderUserDetails(
                  t("common.formFields.email"),
                  <a href={`mailto:${user.email}`}>{user.email}</a>,
                  false,
                  { fontWeight: theme.typography.fontWeightRegular }
                )}
                {renderUserDetails(
                  t("common.formFields.identifier"),
                  user.loginMethodIdentifier ?? "...",
                  true
                )}
                {renderUserDetails(
                  t("common.formFields.birthDate"),
                  user.birthDate
                    ? moment(user.birthDate).format("yyyy-MM-DD")
                    : "..."
                )}
                {renderUserDetails(
                  t("common.formFields.phoneNumber"),
                  user.phoneNumber ?? "..."
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card className={styles.fullHeight}>
              <CardContent>
                <Box sx={{ display: "flex" }} pb={3}>
                  <Box flexGrow={1}>
                    <div>
                      <Typography
                        variant={"caption"}
                        color={"textSecondary"}
                        style={{ textTransform: "uppercase" }}
                      >
                        Employee
                      </Typography>
                    </div>
                    <ButtonGroup
                      variant="contained"
                      size={"small"}
                      aria-label={t("common.tooltip.employee")}
                      style={{ boxShadow: "none" }}
                    >
                      <Button
                        variant={"outlined"}
                        className={`${
                          state.user.employee
                            ? styles.employeeBtnSelected
                            : styles.employeeBtn
                        }`}
                        onClick={() => onEmployeeUpdate(true)}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={"outlined"}
                        className={`${
                          !state.user.employee
                            ? styles.employeeBtnSelected
                            : styles.employeeBtn
                        }`}
                        onClick={() => onEmployeeUpdate(false)}
                      >
                        No
                      </Button>
                    </ButtonGroup>
                  </Box>
                  <div>
                    <Settings className={styles.sectionIconWarning} />
                  </div>
                </Box>
                {renderUserDetails(
                  t("common.messages.allowPush"),
                  user.allowPushCommunication ? "Yes" : "No"
                )}
                {renderUserDetails(
                  t("common.formFields.authenticationType"),
                  user.loginMethod.name
                )}
                {renderUserDetails(
                  t("common.messages.creditCard"),
                  user.hasRegisteredCreditCard ? "Yes" : "No"
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Card className={styles.fullHeight}>
              <CardContent>
                <Box sx={{ display: "flex" }} pb={3}>
                  <Box flexGrow={1}>
                    <div>
                      <Typography
                        variant={"caption"}
                        color={"textSecondary"}
                        style={{ textTransform: "uppercase" }}
                      >
                        Average Purchase Amount
                      </Typography>
                    </div>
                    <Typography variant={"h6"}>
                      {loadingPurchases ? (
                        <CircularProgress size={18} />
                      ) : (
                        user.averagePurchaseAmount.toFixed(2)
                      )}
                    </Typography>
                  </Box>
                  <div>
                    <Equalizer className={styles.sectionIconPrimary} />
                  </div>
                </Box>
                {renderUserDetails(
                  t("common.messages.doorOpenings"),
                  user.openDoorEventCount
                )}
                {renderUserDetails(
                  t("common.tabs.payments"),
                  user.paymentEventCount
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );

      const renderUserDetails = (
        propertyName: string,
        value: any,
        secured = false,
        style: any = {}
      ) => (
        <Grid container className={styles.userDetailsItem}>
          <Grid item xs={6} className={styles.detailsCell}>
            <div className={styles.detailsNameColumn}>{propertyName}</div>
          </Grid>
          <Grid item xs={6} className={styles.detailsCell}>
            {secured ? (
              <div className={styles.detailsDataColumn}>
                <SecuredField>{value}</SecuredField>
              </div>
            ) : (
              <div className={styles.detailsDataColumn} style={style}>
                {value}
              </div>
            )}
          </Grid>
        </Grid>
      );

      const renderReceipts = (screenSize: "mobile" | "desktop") => (
        <Grid container>
          <Grid
            item
            xs={12}
            className={
              screenSize === "desktop" ? styles.receiptsTableDesktop : undefined
            }
          >
            <ReceiptsCollection
              receipts={user.receipts}
              onRefund={id => {
                setfullOrPartialModalVisible(true);
                setRefundReceipt(id);
              }}
              screenSize={screenSize}
            />
          </Grid>
        </Grid>
      );

      return (
        <div>
          {state.type === "SHOW_ERROR" &&
            showAlert("An error occurred", state.message, true)}
          {state.user.status === UserStatus.Locked &&
            showAlert(
              "Account is locked",
              `This account is locked${
                state.user.lockoutReason ? ": " + state.user.lockoutReason : ""
              }`,
              false
            )}
          {state.user.status === UserStatus.Suspicious && (
            <Alert
              severity={"warning"}
              message="Account is showing suspicious activity"
              visible={true}
            />
          )}
          {state.user.status === UserStatus.Verified && (
            <Alert
              severity={"success"}
              message="Account has been verified"
              visible={true}
            />
          )}

          <div className={styles.userPageContainer} id={"DrawerContainer"}>
            <TabContext value={selectedTab}>
              <div
                style={{
                  boxShadow:
                    "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)"
                }}
              >
                <Grid container className={styles.tabsUpperContainer}>
                  <Box display={"flex"} alignItems={"center"} flexGrow={1}>
                    <Avatar
                      style={{
                        marginRight: theme.spacing(2),
                        padding: theme.spacing(3)
                      }}
                      alt={`${user.firstName} ${user.lastName}`}
                    >
                      {user.firstName
                        ? `${user.firstName.charAt(0)}${user.lastName.charAt(
                            0
                          )}`
                        : undefined}
                    </Avatar>
                    <Typography component={"span"}>
                      <Typography
                        variant={"subtitle1"}
                        style={{ lineHeight: 1.1 }}
                      >
                        {`${user.firstName} ${user.lastName}`}
                        <IconButton
                          edge="start"
                          size={"small"}
                          style={{ marginLeft: "0.3rem" }}
                          color="inherit"
                          onClick={e => {
                            navigator.clipboard
                              .writeText(
                                `${location.protocol}//${location.host}/users/${user.id}`
                              )
                              .then(_ => setShowCopiedUserId(true));
                          }}
                          aria-label="copy"
                        >
                          <ContentCopy style={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Typography>

                      <Typography variant={"body2"} color={"textSecondary"}>
                        {user.address && user.zipCode && user.city
                          ? `${user.address}, ${user.zipCode} ${user.city}`
                          : "..."}
                      </Typography>
                    </Typography>
                  </Box>
                  <Box
                    position={"relative"}
                    display={"flex"}
                    justifyContent={"end"}
                  >
                    <Tooltip title={t("common.actions.editUser")}>
                      <IconButton
                        onClick={toggleActionsDrawer}
                        color={"primary"}
                      >
                        <MenuIcon />
                      </IconButton>
                    </Tooltip>
                    <Paper>
                      <SwipeableDrawer
                        anchor={"right"}
                        open={actionsOpen}
                        PaperProps={{
                          style: { position: "fixed", top: "inherit" }
                        }}
                        onClose={() => handleActionsClose()}
                        onOpen={() => handleActionsOpen()}
                      >
                        <Box className={styles.drawerAction}>
                          <Tooltip title={"Close"}>
                            <IconButton onClick={() => handleActionsClose()}>
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Divider variant={"fullWidth"} />
                        <Box className={styles.drawerMenuCategory}>
                          <Typography variant={"body1"} color={"textSecondary"}>
                            User Purchases
                          </Typography>
                        </Box>
                        <MenuItem
                          disabled={state.type !== "LOADED"}
                          className={styles.drawerMenuItem}
                          onClick={() =>
                            navigate(`/users/${user.id}/purchases/register`)
                          }
                        >
                          <Box className={styles.drawerAction}>
                            <IconButton>
                              <AddShoppingCart />
                            </IconButton>
                            <Typography variant={"body2"} component={"div"}>
                              Add purchase
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem
                          className={styles.drawerMenuItem}
                          disabled={state.type !== "LOADED"}
                          onClick={() =>
                            setState({
                              ...state,
                              type: "ADDING_DISCOUNT",
                              discountId: ""
                            })
                          }
                        >
                          <Box className={styles.drawerAction}>
                            <IconButton>
                              <LoyaltyIcon />
                            </IconButton>
                            <Typography variant={"body2"} component={"div"}>
                              Add coupon
                            </Typography>
                          </Box>
                        </MenuItem>
                        <Divider />
                        <Box className={styles.drawerMenuCategory}>
                          <Typography variant={"body1"} color={"textSecondary"}>
                            User Status
                          </Typography>
                        </Box>
                        <MenuItem
                          onClick={onLockOrUnlock}
                          className={styles.drawerMenuItem}
                          disabled={state.type !== "LOADED"}
                        >
                          <Box className={styles.drawerAction}>
                            <IconButton>
                              {state.user.status === UserStatus.Locked ? (
                                <LockOpen />
                              ) : (
                                <Lock />
                              )}
                            </IconButton>
                            <Typography variant={"body2"} component={"div"}>
                              {state.user.status === UserStatus.Locked
                                ? t("common.actions.unlockUser")
                                : t("common.actions.lockUser")}
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem
                          className={styles.drawerMenuItem}
                          disabled={
                            state.type !== "LOADED" ||
                            state.user.status === UserStatus.Locked ||
                            state.user.status === UserStatus.Verified
                          }
                          onClick={() =>
                            setUpdateStatusVisible({
                              display: true,
                              status: UserStatus.Verified
                            })
                          }
                        >
                          <Box className={styles.drawerAction}>
                            <IconButton>
                              <VerifiedUser />
                            </IconButton>
                            <Typography variant={"body2"} component={"div"}>
                              {t("common.actions.verifyUser")}
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem
                          className={styles.drawerMenuItem}
                          disabled={
                            state.type !== "LOADED" ||
                            state.user.status === UserStatus.Locked ||
                            state.user.status === UserStatus.Suspicious
                          }
                          onClick={() =>
                            setUpdateStatusVisible({
                              display: true,
                              status: UserStatus.Suspicious
                            })
                          }
                        >
                          <Box className={styles.drawerAction}>
                            <IconButton>
                              <Report />
                            </IconButton>
                            <Typography variant={"body2"} component={"div"}>
                              Mark as suspicious
                            </Typography>
                          </Box>
                        </MenuItem>

                        <MenuItem
                          className={styles.drawerMenuItem}
                          disabled={state.type !== "LOADED"}
                          onClick={() => setSendNotificationModelVisible(true)}
                        >
                          <Box className={styles.drawerAction}>
                            <IconButton>
                              <Campaign />
                            </IconButton>
                            <Typography variant={"body2"} component={"div"}>
                              Send notification
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem
                          className={styles.drawerMenuItem}
                          disabled={state.type !== "LOADED"}
                          onClick={() =>
                            setCommentState({ ...commentState, display: true })
                          }
                        >
                          <Box className={styles.drawerAction}>
                            <IconButton>
                              <Badge
                                badgeContent={commentState.comments?.length}
                                color={"secondary"}
                              >
                                <Message />
                              </Badge>
                            </IconButton>
                            <Typography variant={"body2"} component={"div"}>
                              Comments
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem
                          className={styles.drawerMenuItem}
                          disabled={state.type !== "LOADED"}
                          onClick={onDelete}
                        >
                          <Box className={styles.drawerAction}>
                            <IconButton>
                              <Delete />
                            </IconButton>
                            <Typography variant={"body2"} component={"div"}>
                              Delete user
                            </Typography>
                          </Box>
                        </MenuItem>
                      </SwipeableDrawer>
                    </Paper>
                  </Box>
                </Grid>
                <Divider color={theme.palette.primary.dark} />
                <TabList
                  onChange={selectTab}
                  variant={"scrollable"}
                  scrollButtons={"on"}
                  style={{ backgroundColor: "white" }}
                >
                  <Tab
                    label={t("common.messages.details")}
                    value={"userDetails"}
                  ></Tab>
                  <Tab
                    label={t("common.messages.purchases")}
                    value={"userPurchases"}
                  ></Tab>
                  <Tab
                    label={t("common.messages.events")}
                    value={"userEvents"}
                  ></Tab>
                  <Tab
                    label={t("common.messages.adminEvents")}
                    value={"adminEvents"}
                  ></Tab>
                </TabList>
              </div>

              <TabPanel
                value={"userDetails"}
                id={"userDetails"}
                className={styles.tabPanel}
              >
                <section>
                  <Box pb={"1.5rem"}>
                    <Hidden smUp>{renderUserDetailsSection("mobile")}</Hidden>
                    <Hidden xsDown>
                      {renderUserDetailsSection("desktop")}
                    </Hidden>
                  </Box>
                </section>
                <section>
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={6}>
                      <UserSectionCard
                        icon={<Loyalty className={styles.sectionIconSuccess} />}
                        title={t("common.messages.coupons")}
                      >
                        {user.discounts?.length > 0 ? (
                          <>
                            <Hidden smUp>
                              <DiscountsCollection
                                discounts={user.discounts}
                                screenSize="mobile"
                              />
                            </Hidden>
                            <Hidden xsDown>
                              <DiscountsCollection
                                discounts={user.discounts}
                                screenSize="desktop"
                              />
                            </Hidden>
                          </>
                        ) : (
                          <Empty message="No coupons are added to this user" />
                        )}
                      </UserSectionCard>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <UserSectionCard
                        icon={
                          <Campaign className={styles.sectionIconPrimary} />
                        }
                        title={t("common.messages.notifications")}
                      >
                        {user.notifications?.length > 0 ? (
                          <>
                            <Hidden smUp>
                              <NotificationsCollection
                                notifications={user.notifications}
                                screenSize="mobile"
                              />
                            </Hidden>
                            <Hidden xsDown>
                              <NotificationsCollection
                                notifications={user.notifications}
                                screenSize="desktop"
                              />
                            </Hidden>
                          </>
                        ) : (
                          <Empty message="No notifications have been sent to this user" />
                        )}
                      </UserSectionCard>
                    </Grid>
                  </Grid>
                </section>
              </TabPanel>
              <TabPanel
                value={"userPurchases"}
                id={"userPurchases"}
                className={styles.tabPanel}
              >
                {loadingPurchases ? (
                  <div style={{ height: mobile ? "100%" : 500 }}>
                    <Progress />
                  </div>
                ) : (
                  <>
                    <section className={styles.userDetailsSection}>
                      <UserSectionCard
                        icon={
                          <Equalizer className={styles.sectionIconPrimary} />
                        }
                        title={t("common.statistics.purchaseStatistics")}
                      >
                        <Line type={Line} data={data} />
                      </UserSectionCard>
                    </section>
                    <section className={styles.userDetailsSection}>
                      <UserSectionCard
                        icon={
                          <Equalizer className={styles.sectionIconSuccess} />
                        }
                        title={t("common.messages.receipts")}
                      >
                        {user.receipts?.length > 0 ? (
                          <>
                            <Hidden smUp>{renderReceipts("mobile")}</Hidden>
                            <Hidden xsDown>{renderReceipts("desktop")}</Hidden>
                          </>
                        ) : (
                          <Empty message="Hmm, this user does not seem to have bought anything..." />
                        )}
                      </UserSectionCard>
                    </section>
                    <section className={styles.userDetailsSection}>
                      <UserSectionCard
                        icon={
                          <Fastfood className={styles.sectionIconPrimary} />
                        }
                        title={t("common.messages.top20PurchasedProducts")}
                      >
                        {user.topPurchasedProducts?.length > 0 ? (
                          <>
                            <Hidden smUp>
                              <Top20Collection
                                products={user.topPurchasedProducts}
                                screenSize="mobile"
                              />
                            </Hidden>
                            <Hidden xsDown>
                              <Top20Collection
                                products={user.topPurchasedProducts}
                                screenSize="desktop"
                              />
                            </Hidden>
                          </>
                        ) : (
                          <Empty message="Hmm, this user does not seem to have bought anything..." />
                        )}
                      </UserSectionCard>
                    </section>
                  </>
                )}
              </TabPanel>
              <TabPanel
                value={"userEvents"}
                id={"userEvents"}
                className={styles.tabPanel}
              >
                <section className={styles.userDetailsSection}>
                  <Card>
                    <CardContent>
                      <Hidden smUp>
                        <EventsCollection
                          userId={user.id}
                          screenSize="mobile"
                        />
                      </Hidden>
                      <Hidden xsDown>
                        <EventsCollection
                          userId={user.id}
                          screenSize="desktop"
                        />
                      </Hidden>
                    </CardContent>
                  </Card>
                </section>
              </TabPanel>
              <TabPanel
                value={"adminEvents"}
                id={"adminEvents"}
                className={styles.tabPanel}
              >
                <section className={styles.userDetailsSection}>
                  <Card>
                    <CardContent>
                      <AdministratorEventsCollection
                        loading={historyState.loading}
                        administratorEvents={historyState.administratorEvents}
                      />
                    </CardContent>
                  </Card>
                </section>
              </TabPanel>
            </TabContext>
          </div>

          <Modal
            title=""
            open={fullOrPartialModalVisible}
            actions={[
              {
                text: "Cancel",
                type: "cancel",
                action: () => {
                  setfullOrPartialModalVisible(false);
                }
              }
            ]}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 15
              }}
            >
              Do you want to make a full refund or partial refund?
            </div>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                color={"primary"}
                style={{ marginRight: 10 }}
                onClick={() => {
                  setFullRefundCofirmDialogVisble(true);
                }}
              >
                Full refund
              </Button>
              <Button
                variant="contained"
                color={"primary"}
                onClick={() => {
                  setRefundModelVisible(true);
                  setfullOrPartialModalVisible(false);
                }}
              >
                Partial refund
              </Button>
            </div>
          </Modal>

          <ConfirmDialog
            visible={fullRefundCofirmDialogVisble}
            titleText={t("common.messages.fullRefund")}
            bodyText="Are you sure you want to make a full refund on this purchase?"
            confirmText={"Yes"}
            onConfirm={async () => {
              const receipt = state.user.receipts.find(
                r => r.id == refundReceipt
              );
              if (receipt == undefined) {
                return false;
              }

              const response = await UserApi.refund(userId, receipt, true);

              if (response.status == 200) {
                setFullRefundCofirmDialogVisble(false);
                setfullOrPartialModalVisible(false);
                setRefundReceiptErrorMessage("");
              } else {
                const { detail } = await response.json();
                setRefundReceiptErrorMessage(detail);
              }

              return true;
            }}
            onCancel={() => {
              setFullRefundCofirmDialogVisble(false);
            }}
          />

          <RefundModal
            visible={refundModalVisible}
            onOk={async () => {
              if (state.type !== "LOADED") return;

              const receipt = state.user.receipts.find(
                r => r.id == refundReceipt
              );
              if (receipt == undefined) {
                return;
              }

              const response = await UserApi.refund(userId, receipt, false);

              if (response.status == 200) {
                setRefundModelVisible(false);
                setRefundReceiptErrorMessage("");
              } else {
                const { detail } = await response.json();
                setRefundReceiptErrorMessage(detail);
              }
            }}
            onCancel={() => {
              setRefundReceiptErrorMessage("");
              setRefundModelVisible(false);
            }}
            onSaveReceiptItem={receiptItem => {
              if (state.type !== "LOADED") return;

              setState({
                type: "LOADED",
                user: {
                  ...state.user,
                  receipts: state.user.receipts.map(receipt => {
                    receipt.items = receipt.items.map(item => {
                      return item.id === receiptItem.id ? receiptItem : item;
                    });
                    return receipt;
                  })
                }
              });
            }}
            receiptItems={
              user.receipts?.filter(receipt => receipt.id === refundReceipt)[0]
                ?.items
            }
            errorMessage={refundReceiptErrorMessage}
          />

          <AddDiscountModal
            visible={state.type === "ADDING_DISCOUNT"}
            onOk={() => {
              if (state.type === "ADDING_DISCOUNT") {
                if (
                  !state.discountId ||
                  state.discountId === "" ||
                  state.discountId === "default"
                ) {
                  return setState({
                    ...state,
                    type: "SHOW_ERROR",
                    message: "A valid coupon must be selected!"
                  });
                }

                UserApi.addDiscount(userId, state.discountId).then(result => {
                  switch (result.type) {
                    case "SUCCESS":
                      return setState({
                        type: "LOADED",
                        user: result.data
                      });
                    case "VALIDATION_FAILED":
                    case "DATABASE_ERROR":
                      return setState({
                        ...state,
                        type: "SHOW_ERROR",
                        message: result.reason
                      });
                    default:
                      return setState({
                        ...state,
                        type: "SHOW_ERROR",
                        message: "Something went wrong!"
                      });
                  }
                });
              }
            }}
            onCancel={() => setState({ ...state, type: "LOADED" })}
            onSelectDiscount={discountId =>
              setState({
                ...state,
                type: "ADDING_DISCOUNT",
                discountId
              })
            }
          />

          <SendNotificationModal
            visible={sendNotificationModelVisible}
            user={state.user}
            onCloseDialog={() => setSendNotificationModelVisible(false)}
          />

          <AddCommentModal
            visible={commentState.display}
            comments={commentState.comments}
            user={state.user}
            onCloseDialog={success => {
              setCommentState({ ...commentState, display: false });
              if (success) {
                setReloadHistory(true);
              }
            }}
          />

          <LockUserModal
            visible={lockUserModalVisible}
            user={state.user}
            onCloseDialog={lockoutReason => {
              setReloadHistory(false);
              setLockUserModalVisible(false);
              if (lockoutReason) {
                setState({
                  ...state,
                  user: {
                    ...state.user,
                    lockoutReason,
                    status: UserStatus.Locked
                  }
                });
                setReloadHistory(true);
              }
            }}
          />

          <UnlockUserModal
            visible={unlockUserModalVisible}
            onOk={async () => {
              setReloadHistory(false);
              const result = await UserApi.unlock(state.user.id);

              setUnlockUserModalVisible(false);

              if (isOk(result)) {
                setReloadHistory(true);
                return setState({
                  ...state,
                  user: {
                    ...state.user,
                    lockoutReason: undefined,
                    status: UserStatus.None
                  }
                });
              }

              return setState({
                type: "SHOW_ERROR",
                user: state.user,
                message: `An error occurred when unlocking user. ${result.details.status} - ${result.details.title} - ${result.details.detail}`
              });
            }}
            t={t}
            onCancel={() => setUnlockUserModalVisible(false)}
          />

          <DeleteUserModal
            visible={deleteUserModalVisible}
            onOk={async () => {
              const result = await UserApi.deleteUser(state.user.id);

              setDeleteUserModalVisible(false);

              if (isOk(result)) {
                return setState({
                  type: "LOADED",
                  user: result.value
                });
              }

              return setState({
                type: "SHOW_ERROR",
                user: state.user,
                message: `An error occurred when deleting user. ${result.details.status} - ${result.details.title} - ${result.details.detail}`
              });
            }}
            t={t}
            onCancel={() => setDeleteUserModalVisible(false)}
          />

          <ConfirmDialog
            visible={updateStatusVisible.display}
            titleText={
              updateStatusVisible.status === UserStatus.Verified
                ? t("common.actions.verifyUser")
                : t("common.actions.markAsSuspicious")
            }
            bodyText={`Do you want to mark the user as ${
              updateStatusVisible.status === UserStatus.Verified
                ? "verified"
                : "suspicious"
            }?`}
            confirmText={"Confirm"}
            onConfirm={async () => {
              const status = updateStatusVisible.status;
              setUpdateStatusVisible({
                status: UserStatus.None,
                display: false
              });
              await onUpdate(status);
              return true;
            }}
            onCancel={() => {
              setUpdateStatusVisible({
                display: false,
                status: UserStatus.None
              });
            }}
          />

          <Snackbar
            open={showCopiedUserId}
            autoHideDuration={1000}
            onClose={() => setShowCopiedUserId(false)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center"
            }}
            TransitionComponent={props => <Slide {...props} direction="down" />}
          >
            <lab.Alert severity="success" style={{ width: "100%" }}>
              Copied link to user
            </lab.Alert>
          </Snackbar>
        </div>
      );
  }
};
